import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, InputAdornment,
  LinearProgress, List, ListItemButton, ListItemText, MenuItem, Stack, Table, TableBody,
  TableCell, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from "@mui/material";
import { Bookmark, Inbox, Plus, RefreshCw, RotateCcw, Save, Search, Share2, Trash2, X } from "lucide-react";
import { HelpdeskPage, useHd } from "./HelpdeskContext";
import { hd, errorMessage } from "./api";
import { useSocketEvent } from "./socket";
import NewTicketDialog from "./NewTicketDialog";
import { ChannelIcon, PriorityBadge, SlaBadge, StatusBadge } from "./ui";
import { EmptyState, GlassCard, PageHeader, PersonAvatar, T, TYPE, formatRelative, labelSx, primaryButtonSx, ghostButtonSx } from "../user/complaints/shared";

export const VIEWS = [
  ["all", "All Tickets"], ["mine", "My Tickets"], ["unassigned", "Unassigned"], ["open", "Open"], ["new", "New"], ["pending", "Pending"],
  ["in_progress", "In Progress"], ["waiting_customer", "Waiting for Customer"], ["waiting_agent", "Waiting for Agent"], ["escalated", "Escalated"],
  ["overdue", "Overdue"], ["sla_breached", "SLA Breached"], ["ai_handled", "AI Handled"], ["human_handled", "Human Handled"],
  ["resolved", "Resolved"], ["closed", "Closed"],
];

const FILTER_KEYS = ["status", "priority", "category", "team", "assignee", "channel", "tag", "from", "to"];

function InboxView() {
  const ctx = useHd();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "all";
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0 });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState(params.get("q") || "");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const page = Number(params.get("page") || 1);
  const limit = Number(params.get("limit") || 25);
  const sort = params.get("sort") || "updatedAt";
  const order = params.get("order") || "desc";

  const setParam = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v === "" || v == null ? next.delete(k) : next.set(k, v)));
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const query = useMemo(() => {
    const out = { view, page, limit, sort, order };
    FILTER_KEYS.forEach((k) => params.get(k) && (out[k] = params.get(k)));
    if (params.get("q")) out.q = params.get("q");
    return out;
  }, [params, view, page, limit, sort, order]);

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const [r, c] = await Promise.all([view === "trash" ? { rows: [], pagination: { total: 0 } } : hd.tickets(query), hd.viewCounts()]);
      if (id !== reqId.current) return;
      setRows(r.rows);
      setPagination(r.pagination);
      setCounts(c);
      setError("");
    } catch (err) {
      if (id === reqId.current) setError(errorMessage(err));
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [query, view]);

  useEffect(() => {
    load();
  }, [load]);
  // a new page/filter is a new result set: drop the selection
  useEffect(() => setSelected([]), [query]);

  const loadViews = useCallback(() => hd.savedViews().then(setSavedViews).catch(() => {}), []);
  useEffect(() => {
    loadViews();
  }, [loadViews]);
  const currentParams = () => Object.fromEntries([...params.entries()].filter(([k]) => k !== "page"));
  const sameParams = (a, b) => JSON.stringify(Object.entries(a || {}).sort()) === JSON.stringify(Object.entries(b || {}).sort());
  const activeSaved = savedViews.find((v) => sameParams(v.params, currentParams()));

  // debounce search box into the URL
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get("q") || "") !== q) setParam({ q });
    }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // realtime: refresh (throttled) when tickets change anywhere in the workspace
  const pending = useRef(null);
  useSocketEvent(["ticket.created", "ticket.updated", "ticket.assigned", "ticket.deleted"], () => {
    if (pending.current) return;
    pending.current = setTimeout(() => {
      pending.current = null;
      load();
    }, 1500);
  });
  useEffect(() => () => clearTimeout(pending.current), []);

  const activeFilters = FILTER_KEYS.filter((k) => params.get(k));
  const sortHeader = (key, label) => (
    <TableSortLabel active={sort === key} direction={sort === key ? order : "desc"} onClick={() => setParam({ sort: key, order: sort === key && order === "desc" ? "asc" : "desc" })}>
      {label}
    </TableSortLabel>
  );

  return (
    <Box>
      <PageHeader
        icon={Inbox}
        title="Helpdesk Tickets"
        subtitle="Manage, prioritize, and resolve customer support tickets across all communication channels"
        actions={
          <>
            <Button variant="outlined" sx={ghostButtonSx} startIcon={<RefreshCw size={15} />} onClick={load}>Refresh</Button>
            {ctx.can("tickets.create") && <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={16} />} onClick={() => setShowNew(true)}>New ticket</Button>}
          </>
        }
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "230px minmax(0,1fr)" }, gap: 2, alignItems: "start" }}>
        <GlassCard
          sx={{
            p: 1.5,
            height: { md: "calc(100vh - 175px)" },
            maxHeight: { md: "calc(100vh - 175px)" },
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#cbd5e1 transparent",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "4px" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#cbd5e1" },
          }}
        >
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.5px", color: "#98A2B3", px: 1, py: 0.5, textTransform: "uppercase" }}>
            Inbox Views
          </Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {[
              ["all", "All Tickets"],
              ["mine", "My Tickets"],
              ["unassigned", "Unassigned"],
              ["open", "Open"],
            ].map(([key, label]) => (
              <ListItemButton key={key} selected={view === key} onClick={() => setParam({ view: key })}
                sx={{ borderRadius: "8px", mb: 0.25, "&.Mui-selected": { bgcolor: T.brandSoft, color: T.brand, "& .MuiTypography-root": { fontWeight: 700 } } }}>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: TYPE.small }} />
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, fontWeight: 600 }}>{counts[key] ?? ""}</Typography>
              </ListItemButton>
            ))}
          </List>

          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.5px", color: "#98A2B3", px: 1, py: 0.5, textTransform: "uppercase" }}>
            Queues & SLA
          </Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {[
              ["pending", "Pending"],
              ["in_progress", "In Progress"],
              ["waiting_customer", "Waiting Customer"],
              ["escalated", "Escalated"],
              ["overdue", "Overdue"],
              ["sla_breached", "SLA Breached"],
            ].map(([key, label]) => (
              <ListItemButton key={key} selected={view === key} onClick={() => setParam({ view: key })}
                sx={{ borderRadius: "8px", mb: 0.25, "&.Mui-selected": { bgcolor: T.brandSoft, color: T.brand, "& .MuiTypography-root": { fontWeight: 700 } } }}>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: TYPE.small }} />
                <Typography sx={{ fontSize: TYPE.micro, color: key === "overdue" || key === "sla_breached" ? "#B42318" : T.inkMuted, fontWeight: 600 }}>{counts[key] ?? ""}</Typography>
              </ListItemButton>
            ))}
          </List>

          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.5px", color: "#98A2B3", px: 1, py: 0.5, textTransform: "uppercase" }}>
            Archive & Status
          </Typography>
          <List dense disablePadding>
            {[
              ["ai_handled", "AI Handled"],
              ["resolved", "Resolved"],
              ["closed", "Closed"],
            ].map(([key, label]) => (
              <ListItemButton key={key} selected={view === key} onClick={() => setParam({ view: key })}
                sx={{ borderRadius: "8px", mb: 0.25, "&.Mui-selected": { bgcolor: T.brandSoft, color: T.brand, "& .MuiTypography-root": { fontWeight: 700 } } }}>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: TYPE.small }} />
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, fontWeight: 600 }}>{counts[key] ?? ""}</Typography>
              </ListItemButton>
            ))}
            {ctx.can("tickets.delete") && (
              <ListItemButton selected={view === "trash"} onClick={() => setParam({ view: "trash" })}
                sx={{ borderRadius: "8px", mt: 0.5, "&.Mui-selected": { bgcolor: "#FEF3F2", color: "#B42318", "& .MuiTypography-root": { fontWeight: 700 } } }}>
                <Trash2 size={14} style={{ marginRight: 8 }} />
                <ListItemText primary="Trash" primaryTypographyProps={{ fontSize: TYPE.small }} />
              </ListItemButton>
            )}
          </List>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" alignItems="center" sx={{ px: 1, mb: 0.5 }}>
            <Typography sx={{ ...labelSx, flexGrow: 1 }}>Saved views</Typography>
            <Tooltip title="Save current filters as a view"><IconButton size="small" onClick={() => setSaveViewOpen(true)}><Save size={14} /></IconButton></Tooltip>
          </Stack>
          <List dense disablePadding>
            {!savedViews.length && <Typography sx={{ px: 1, fontSize: TYPE.micro, color: T.inkMuted }}>Set filters, then save them here.</Typography>}
            {savedViews.map((v) => (
              <ListItemButton key={v._id} selected={activeSaved?._id === v._id} onClick={() => setParams(new URLSearchParams(v.params || {}))}
                sx={{ borderRadius: "8px", mb: 0.25, pr: 0.5, "&.Mui-selected": { bgcolor: T.brandSoft, color: T.brand, "& .MuiTypography-root": { fontWeight: 700 } } }}>
                <Bookmark size={13} style={{ marginRight: 8, flexShrink: 0 }} />
                <ListItemText primary={v.name} primaryTypographyProps={{ fontSize: TYPE.small, noWrap: true }} />
                {v.shared && <Tooltip title="Shared with all agents"><Share2 size={12} color={T.inkMuted} /></Tooltip>}
                {(v.mine || ctx.can("settings.manage")) && (
                  <IconButton size="small" onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Delete view "${v.name}"?`)) hd.deleteSavedView(v._id).then(loadViews).catch((err) => ctx.notify(errorMessage(err), "error"));
                  }}><Trash2 size={12} /></IconButton>
                )}
              </ListItemButton>
            ))}
          </List>
        </GlassCard>

        {view === "trash" ? <TrashView /> : (
        <GlassCard
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            height: { md: "calc(100vh - 175px)" },
            maxHeight: { md: "calc(100vh - 175px)" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" spacing={1.25} sx={{ p: 2, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" placeholder="Search key, subject, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 260, flexGrow: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment> }} />
            <FilterSelect label="Status" value={params.get("status")} onChange={(v) => setParam({ status: v })} options={ctx.statuses.map((s) => [s._id, s.name])} />
            <FilterSelect label="Priority" value={params.get("priority")} onChange={(v) => setParam({ priority: v })} options={ctx.priorities.map((p) => [p._id, p.name])} />
            <FilterSelect label="Category" value={params.get("category")} onChange={(v) => setParam({ category: v })} options={ctx.categories.filter((c) => c.level === "category").map((c) => [c._id, c.name])} />
            <FilterSelect label="Team" value={params.get("team")} onChange={(v) => setParam({ team: v })} options={ctx.teams.map((t) => [t._id, t.name])} />
            <FilterSelect label="Agent" value={params.get("assignee")} onChange={(v) => setParam({ assignee: v })} options={[["none", "Unassigned"], ...ctx.agents.map((a) => [a.memberId, a.name])]} />
            <FilterSelect label="Channel" value={params.get("channel")} onChange={(v) => setParam({ channel: v })} options={["email", "chat", "whatsapp", "voice", "web", "api"].map((c) => [c, c])} />
            <FilterSelect label="Tag" value={params.get("tag")} onChange={(v) => setParam({ tag: v })} options={ctx.tags.map((t) => [t, t])} />
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={params.get("from") || ""} onChange={(e) => setParam({ from: e.target.value })} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={params.get("to") || ""} onChange={(e) => setParam({ to: e.target.value })} />
            {activeFilters.length > 0 && (
              <Button size="small" startIcon={<X size={14} />} onClick={() => setParam(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""])))}>Clear</Button>
            )}
          </Stack>
          {selected.length > 0 && <BulkBar ids={selected} onClear={() => setSelected([])} onDone={() => { setSelected([]); load(); }} />}
          {loading && <LinearProgress sx={{ height: 2, flexShrink: 0 }} />}
          {error ? (
            <Typography sx={{ p: 3, color: "#B42318" }}>{error}</Typography>
          ) : !loading && !rows.length ? (
            <Box sx={{ py: 8, textAlign: "center", px: 2, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
              <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "rgba(99, 102, 241, 0.1)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "rgb(99, 102, 241)", mb: 1.5 }}>
                <Inbox size={24} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: T.ink, mb: 0.5 }}>No support tickets in this view</Typography>
              <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, maxWidth: 440, mx: "auto", mb: 2.5 }}>
                Customer requests from connected email, live chat, WhatsApp, and agents will appear here.
              </Typography>
              {ctx.can("tickets.create") && (
                <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={16} />} onClick={() => setShowNew(true)}>
                  New Ticket
                </Button>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e1 transparent",
                "&::-webkit-scrollbar": { width: "6px", height: "6px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "4px" },
                "&::-webkit-scrollbar-thumb:hover": { background: "#cbd5e1" },
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow sx={{ "& th": { ...labelSx, bgcolor: `${T.surfaceHeader} !important`, py: 1.25 } }}>
                    <TableCell padding="checkbox" sx={{ bgcolor: `${T.surfaceHeader} !important` }}>
                      <Checkbox size="small" checked={rows.length > 0 && selected.length === rows.length} indeterminate={selected.length > 0 && selected.length < rows.length}
                        onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r._id) : [])} inputProps={{ "aria-label": "Select all tickets on this page" }} />
                    </TableCell>
                    <TableCell sx={{ bgcolor: `${T.surfaceHeader} !important` }}>{sortHeader("key", "Ticket")}</TableCell>
                    <TableCell sx={{ bgcolor: `${T.surfaceHeader} !important` }}>Subject</TableCell>
                    <TableCell sx={{ bgcolor: `${T.surfaceHeader} !important` }}>Customer</TableCell>
                    <TableCell sx={{ bgcolor: `${T.surfaceHeader} !important` }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: `${T.surfaceHeader} !important` }}>{sortHeader("priority", "Priority")}</TableCell>
                    <TableCell sx={{ bgcolor: `${T.surfaceHeader} !important` }}>{sortHeader("due", "SLA")}</TableCell>
                    <TableCell sx={{ bgcolor: `${T.surfaceHeader} !important` }}>Assignee</TableCell>
                    <TableCell sx={{ bgcolor: `${T.surfaceHeader} !important` }}>{sortHeader("updatedAt", "Updated")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow key={t._id} hover selected={selected.includes(t._id)} sx={{ cursor: "pointer", "& td": { py: 1.2, fontSize: TYPE.small } }} onClick={() => navigate(`/helpdesk/tickets/${t._id}`)}>
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox size="small" checked={selected.includes(t._id)} onChange={(e) => setSelected((x) => (e.target.checked ? [...x, t._id] : x.filter((id) => id !== t._id)))} inputProps={{ "aria-label": `Select ${t.key}` }} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700, color: T.brand }}>
                        <Stack direction="row" spacing={0.75} alignItems="center"><ChannelIcon channel={t.channel} /><span>{t.key}</span></Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography noWrap sx={{ fontSize: TYPE.small, fontWeight: 600, color: T.ink }}>{t.subject}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.3 }}>
                          {t.escalated && <Chip size="small" label="Escalated" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#FEF0C7", color: "#B54708" }} />}
                          {t.handledBy === "ai" && <Chip size="small" label="AI" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#F4EBFF", color: "#5925DC" }} />}
                          {(t.tags || []).slice(0, 3).map((tag) => <Chip key={tag} size="small" label={tag} sx={{ height: 18, fontSize: "0.65rem" }} />)}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography noWrap sx={{ fontSize: TYPE.small, maxWidth: 200 }}>{t.requester?.name || t.requester?.email || "—"}</Typography>
                        <Typography noWrap sx={{ fontSize: TYPE.micro, color: T.inkMuted, maxWidth: 200 }}>{t.requester?.email}</Typography>
                      </TableCell>
                      <TableCell><StatusBadge status={t.status} /></TableCell>
                      <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                      <TableCell><SlaBadge ticket={t} compact /></TableCell>
                      <TableCell>
                        {t.assignee?.name ? (
                          <Stack direction="row" spacing={1} alignItems="center"><PersonAvatar name={t.assignee.name} size={22} /><Typography noWrap sx={{ fontSize: TYPE.small, maxWidth: 120 }}>{t.assignee.name}</Typography></Stack>
                        ) : (
                          <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>{t.team?.name ? `${t.team.name} (unassigned)` : "Unassigned"}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", color: T.inkSecondary }}>
                        <Tooltip title={new Date(t.updatedAt).toLocaleString("en-IN")}><span>{formatRelative(t.updatedAt)}</span></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          <TablePagination
            component="div" count={pagination.total || 0} page={Math.max(0, page - 1)} rowsPerPage={limit} rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, p) => setParam({ page: p + 1 })} onRowsPerPageChange={(e) => setParam({ limit: e.target.value, page: 1 })}
            sx={{ flexShrink: 0, borderTop: `1px solid ${T.border}` }}
          />
        </GlassCard>
        )}
      </Box>
      <SaveViewDialog open={saveViewOpen} onClose={() => setSaveViewOpen(false)} params={currentParams()} canShare={ctx.can("tickets.view_all")}
        onSaved={() => { setSaveViewOpen(false); loadViews(); }} />
      {showNew && <NewTicketDialog open onClose={() => setShowNew(false)} onCreated={(t) => { setShowNew(false); navigate(`/helpdesk/tickets/${t._id}`); }} />}
    </Box>
  );
}

// Deleted tickets: restore one by one or in bulk.
const TrashView = () => {
  const ctx = useHd();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    hd.trash({ page: page + 1, limit: 50 }).then((r) => { setRows(r.rows); setTotal(r.pagination.total); setSelected([]); }).catch((err) => ctx.notify(errorMessage(err), "error"));
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [load]);
  const restore = async (ids) => {
    setBusy(true);
    try {
      const r = await hd.bulkTickets({ action: "restore", ids });
      ctx.notify(r.failed?.length ? `${r.message}` : `${r.updated} ticket${r.updated === 1 ? "" : "s"} restored`, r.failed?.length ? "warning" : "success");
      load();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <GlassCard sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, borderBottom: `1px solid ${T.border}` }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>Trash</Typography>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Deleted tickets keep their messages and history until restored.</Typography>
        </Box>
        {selected.length > 0 && <Button size="small" variant="contained" sx={primaryButtonSx} disabled={busy} startIcon={<RotateCcw size={14} />} onClick={() => restore(selected)}>Restore {selected.length}</Button>}
      </Stack>
      {!rows ? <LinearProgress sx={{ height: 2 }} /> : !rows.length ? (
        <EmptyState icon={Trash2} title="Trash is empty" description="Deleted tickets show up here." />
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{ "& th": { ...labelSx, bgcolor: T.surfaceHeader, py: 1.25 } }}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={selected.length === rows.length} indeterminate={selected.length > 0 && selected.length < rows.length}
                    onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r._id) : [])} inputProps={{ "aria-label": "Select all deleted tickets" }} />
                </TableCell>
                <TableCell>Ticket</TableCell><TableCell>Subject</TableCell><TableCell>Customer</TableCell><TableCell>Deleted</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t._id} hover selected={selected.includes(t._id)} sx={{ "& td": { py: 1, fontSize: TYPE.small } }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.includes(t._id)} onChange={(e) => setSelected((x) => (e.target.checked ? [...x, t._id] : x.filter((id) => id !== t._id)))} inputProps={{ "aria-label": `Select ${t.key}` }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{t.key}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}><Typography noWrap sx={{ fontSize: TYPE.small }}>{t.subject}</Typography></TableCell>
                  <TableCell><Typography noWrap sx={{ fontSize: TYPE.small, maxWidth: 180 }}>{t.requester?.name || t.requester?.email || "—"}</Typography></TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", color: T.inkSecondary }}>
                    <Tooltip title={new Date(t.deletedAt).toLocaleString("en-IN")}><span>{formatRelative(t.deletedAt)}{t.deletedBy ? ` by ${t.deletedBy}` : ""}</span></Tooltip>
                  </TableCell>
                  <TableCell align="right"><Button size="small" disabled={busy} startIcon={<RotateCcw size={13} />} onClick={() => restore([t._id])}>Restore</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <TablePagination component="div" count={total} page={page} rowsPerPage={50} rowsPerPageOptions={[50]} onPageChange={(_, p) => setPage(p)} />
    </GlassCard>
  );
};

// Applies one action to every selected ticket; the server checks each ticket
// with the same rules as the single-ticket screen and reports failures.
const BulkBar = ({ ids, onClear, onDone }) => {
  const ctx = useHd();
  const [busy, setBusy] = useState(false);
  const [tag, setTag] = useState("");
  const run = async (action, extra = {}, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const r = await hd.bulkTickets({ action, ids, ...extra });
      const failures = r.failed?.length ? `: ${r.failed.slice(0, 3).map((f) => `${f.key || "ticket"} — ${f.error}`).join("; ")}` : "";
      ctx.notify(`${r.message}${failures}`, r.failed?.length ? "warning" : "success");
      onDone();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };
  const pick = (label, options, onPick, minWidth = 140) => (
    <TextField select size="small" label={label} value="" onChange={(e) => onPick(e.target.value)} sx={{ minWidth }} disabled={busy}>
      {options.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
    </TextField>
  );
  const n = ids.length;
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 2, py: 1.25, bgcolor: T.brandSoft, borderBottom: `1px solid ${T.border}` }}>
      <Typography sx={{ fontSize: TYPE.small, fontWeight: 700, color: T.brand, mr: 1 }}>{n} selected</Typography>
      {ctx.can("tickets.assign") && pick("Assign to", [["__none", "Unassign"], ...ctx.agents.map((a) => [a.memberId, a.name])], (v) => run("assign", { assigneeId: v === "__none" ? null : v }))}
      {ctx.can("tickets.assign") && pick("Team", ctx.teams.map((t) => [t._id, t.name]), (v) => run("assign", { teamId: v }), 120)}
      {pick("Status", ctx.statuses.map((st) => [st._id, st.name]), (v) => run("status", { statusId: v }), 130)}
      {ctx.can("tickets.update") && pick("Priority", ctx.priorities.map((p) => [p._id, p.name]), (v) => run("priority", { priorityId: v }), 120)}
      {ctx.can("tickets.update") && (
        <TextField size="small" label="Add tag" value={tag} onChange={(e) => setTag(e.target.value)} disabled={busy} sx={{ width: 130 }}
          onKeyDown={(e) => { if (e.key === "Enter" && tag.trim()) { run("add_tags", { tags: [tag.trim()] }); setTag(""); } }} />
      )}
      {ctx.can("tickets.resolve") && (
        <Button size="small" disabled={busy} onClick={() => {
          const resolution = window.prompt(`Resolve ${n} ticket(s)? Customers are notified.

Resolution note:`, "");
          if (resolution !== null) run("resolve", { resolution });
        }}>Resolve</Button>
      )}
      {ctx.can("tickets.close") && <Button size="small" disabled={busy} onClick={() => run("close", {}, `Close ${n} ticket(s)?`)}>Close</Button>}
      {ctx.can("tickets.delete") && <Button size="small" color="error" disabled={busy} onClick={() => run("delete", {}, `Delete ${n} ticket(s)?`)}>Delete</Button>}
      <Box sx={{ flexGrow: 1 }} />
      <Button size="small" startIcon={<X size={14} />} onClick={onClear} disabled={busy}>Clear</Button>
    </Stack>
  );
};

const SaveViewDialog = ({ open, onClose, params, canShare, onSaved }) => {
  const ctx = useHd();
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  useEffect(() => {
    if (open) {
      setName("");
      setShared(false);
    }
  }, [open]);
  const save = async () => {
    try {
      await hd.createSavedView({ name, params, shared });
      ctx.notify("View saved");
      onSaved();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Save view</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth size="small" label="View name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1 }} inputProps={{ maxLength: 80 }} />
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1 }}>Saves the current view, filters, search and sort.</Typography>
        {canShare && <FormControlLabel sx={{ mt: 1 }} control={<Checkbox size="small" checked={shared} onChange={(e) => setShared(e.target.checked)} />} label="Share with all agents" />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={!name.trim()} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const FilterSelect = ({ label, value, onChange, options }) => (
  <TextField select size="small" label={label} value={value || ""} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 130 }}>
    <MenuItem value="">Any</MenuItem>
    {options.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
  </TextField>
);

export default function TicketInbox() {
  return (
    <HelpdeskPage>
      <InboxView />
    </HelpdeskPage>
  );
}
